import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Subject, Subscription, combineLatest } from 'rxjs';
import { FirebaseAuthService } from 'src/app/shared/providers/firebase-auth.service';
import { RenderFlowStep, RenderJob } from './render-flow.model';
import { RenderActivity, RenderFlowService, RenderReady, RenderUploading } from './render-flow.service';

const STORAGE_KEY = '3dway.render-notify.v1';
const ICON = 'assets/images/favicons/apple-touch-icon.png';

export interface RenderNotifySettings {
  email: boolean;
  browser: boolean;
}

// A run that has just ended: the preview's or the videos', ready or failed. `job.unseen` says
// whether it ended away from the model's page.
export interface RenderNews {
  job: RenderJob;
  what: 'preview' | 'videos';
  failed: boolean;
}

// What the browser allows: its permission, or 'unsupported' (no Notification API, or a page that
// is not a secure context).
export type RenderBrowserPermission = NotificationPermission | 'unsupported';

// "When your videos are ready" on the rendering step (designer, Figma 28574:58489, 2026-09-24):
// an email and a notification from this browser. Both are the signed-in user's choice on this
// device, kept for their next render.
//
// Email: on unless turned off, to the account's address. The backend sends it when the render ends
// (Slack 2026-09-21); the choice has to reach it (docs/render-landing-port.md, question 15) — the
// mock sends nothing and says so.
// Browser: the Notification API, working now, mock included — but only while a 3DWay tab is open.
// A notification with the browser closed needs web push (a service worker + FCM + the backend).
//
// Every run that ends, the preview's too, is also news$ (the shell's toast for a model you are not
// looking at) and goes into the tab's title while a page of the flow holds it (holdTabTitle): a
// preview takes about two minutes on the real renderer, long enough to look at another tab.
@Injectable()
export class RenderNotifyService implements OnDestroy {
  // Each job's step as last seen: a run that ends is a step going from 'processing' or 'rendering'
  // elsewhere.
  private readonly steps = new Map<string, RenderFlowStep>();
  private subscription?: Subscription;
  private readonly newsSubject = new Subject<RenderNews>();
  public readonly news$ = this.newsSubject.asObservable();
  // A run that ended while this tab was hidden, until the tab is looked at again: the model on
  // screen is never `unseen`, so the profile button's dot would not tell, and neither would the title.
  private readonly missedSubject = new BehaviorSubject<RenderNews | null>(null);

  constructor(
    private readonly flow: RenderFlowService,
    private readonly firebaseAuth: FirebaseAuthService,
    private readonly router: Router,
    private readonly zone: NgZone,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  // The shell starts it: whatever page of the flow is open, a render ending gets its notification.
  // Jobs already finished when it starts are not news.
  public watch(): void {
    if (this.subscription) {
      return;
    }
    this.subscription = this.flow.jobs$.subscribe((jobs) => {
      for (const job of jobs) {
        const before = this.steps.get(job.modelId);
        this.steps.set(job.modelId, job.step);
        const news = RenderNotifyService.newsOf(before, job);
        if (!news) {
          continue;
        }
        if (news.what === 'videos') {
          this.notify(job);
        }
        if (this.document.hidden) {
          this.missedSubject.next(news);
        }
        this.newsSubject.next(news);
      }
    });
  }

  // The tab's title tells what the header tells, for when the tab is not the one in front: an
  // archive unpacking or an upload on its way first ("4% Uploading · 3DWay" — this tab has to stay
  // open for it), then the news ("✓ Preview ready · 3DWay", "✕ Preview failed · 3DWay"), else the
  // work ("45% Rendering · 3DWay"), else the page's own title. The percent is the header ring's
  // (RenderJob.progress). A page of the flow holds it while it is open; the release puts the page's
  // title back.
  public holdTabTitle(): () => void {
    const base = this.document.title;
    const onVisible = () => {
      if (!this.document.hidden) {
        this.missedSubject.next(null);
      }
    };
    this.document.addEventListener('visibilitychange', onVisible);
    const subscription = combineLatest([this.flow.uploading$, this.flow.activity$, this.flow.ready$, this.missedSubject]).subscribe(([uploading, activity, ready, missed]) => {
      this.document.title = RenderNotifyService.titleOf(base, uploading, activity, ready, missed);
    });
    return () => {
      subscription.unsubscribe();
      this.document.removeEventListener('visibilitychange', onVisible);
      this.document.title = base;
    };
  }

  public get email(): string | null {
    return this.firebaseAuth.currentUser?.email || null;
  }

  public get settings(): RenderNotifySettings {
    try {
      const stored = JSON.parse(localStorage.getItem(this.storageKey) || 'null') as Partial<RenderNotifySettings> | null;
      return { email: stored?.email !== false, browser: stored?.browser === true };
    } catch {
      return { email: true, browser: false };
    }
  }

  public get permission(): RenderBrowserPermission {
    const view = this.document.defaultView;
    if (!view || !('Notification' in view) || !view.isSecureContext) {
      return 'unsupported';
    }
    return Notification.permission;
  }

  // On only while the browser still allows it: permission taken back in its settings turns it off.
  public get browserOn(): boolean {
    return this.settings.browser && this.permission === 'granted';
  }

  public setEmail(on: boolean): void {
    this.save({ ...this.settings, email: on });
  }

  // Turning it on asks the browser first; resolves with what it ended up as.
  public async setBrowser(on: boolean): Promise<boolean> {
    if (on && this.permission === 'default') {
      await Notification.requestPermission();
    }
    const allowed = on && this.permission === 'granted';
    this.save({ ...this.settings, browser: allowed });
    return allowed;
  }

  // Only when the user is not already looking at it: another page of the flow (the job is
  // `unseen`), another tab, or another window.
  private notify(job: RenderJob): void {
    if (!this.browserOn || !(job.unseen || this.document.hidden)) {
      return;
    }
    const ready = job.step === 'downloads';
    const notification = new Notification(ready ? `${job.name} is ready` : `${job.name}: the render stopped`, {
      body: ready ? 'Both videos are ready to download.' : 'Open 3DWay to try again. Nothing is charged twice.',
      icon: ICON,
      tag: `3dway-render-${job.modelId}`,
    });
    notification.onclick = () => {
      this.zone.run(() => {
        this.document.defaultView?.focus();
        this.router.navigateByUrl(RenderFlowService.stepUrl(job));
      });
      notification.close();
    };
  }

  private static newsOf(before: RenderFlowStep | undefined, job: RenderJob): RenderNews | null {
    if (before === 'processing' && (job.step === 'preview' || job.step === 'error')) {
      return { job, what: 'preview', failed: job.step === 'error' };
    }
    if (before === 'rendering' && (job.step === 'downloads' || job.step === 'error')) {
      return { job, what: 'videos', failed: job.step === 'error' };
    }
    return null;
  }

  private static titleOf(base: string, uploading: RenderUploading, activity: RenderActivity | null, ready: RenderReady | null, missed: RenderNews | null): string {
    if (uploading === 'unpacking') {
      return `Unpacking · ${base}`;
    }
    if (activity?.uploading) {
      return `${activity.progress}% Uploading · ${base}`;
    }
    const news = ready || (missed && { count: 1, what: missed.what, failed: missed.failed });
    if (news) {
      return `${news.failed ? '✕' : '✓'} ${RenderFlowService.readyText(news)} · ${base}`;
    }
    if (activity) {
      return `${activity.progress}% Rendering · ${base}`;
    }
    return base;
  }

  private save(settings: RenderNotifySettings): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(settings));
    } catch {
      // Private mode / blocked storage: the choice lasts for this page only.
    }
  }

  private get storageKey(): string {
    return `${STORAGE_KEY}:${this.firebaseAuth.currentUser?.uid || 'guest'}`;
  }
}
